import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import { Button } from '@treams/treams-ui/common/button/Button';
import { Modal } from '@treams/treams-ui/common/Modal';
import { OriginPosition, Popover } from '@treams/treams-ui/common/Popover';
import { ImageResizer } from '@treams/treams-ui/form/controls/image-upload/ImageResizer';
import { ImageUpload } from '@treams/treams-ui/form/controls/image-upload/ImageUpload';
import { FormErrorMessage } from '@treams/treams-ui/form/errors/FormErrorMessage';

import { IImageSpecification, ImageFile } from '../../packages/kakapo-ui-kit/types/Image';
import ResourceError from '../../packages/kakapo-ui-services/models/errors/ResourceError';

interface IProps {
	imageSpecification: IImageSpecification;
	onSubmit: (resizedFileBlob: Blob | null) => void;
	onCancel: () => void;
	center?: boolean;
	origin?: OriginPosition;
	hasVideo?: boolean;
}

export default function ImageUploadModalContainer({
	imageSpecification,
	onSubmit,
	onCancel,
	center,
	origin,
	hasVideo,
}: IProps) {
	const { t } = useTranslation();
	const [uploadedFile, setUploadedFile] = useState<ImageFile | null>(null);
	const [resizedFileBlob, setResizedFileBlob] = useState<Blob | null>(null);
	const [error, setError] = useState<Error | null>(null);

	const isSvg = uploadedFile && (uploadedFile as string).includes('data:image/svg+xml');

	const isVideo = uploadedFile && (uploadedFile as string).includes('data:video');

	const handleUpload = (blob: Blob) => {
		try {
			onSubmit(blob);
		} catch (e) {
			const resourceError = e as ResourceError;
			const { errorObject } = resourceError;
			if (errorObject !== undefined) {
				const errArr = errorObject![''];
				setError(new Error(errArr.join(' ')));
			} else {
				setError(e);
			}
		}
	};

	const uploadQuillMedia = async () => {
		const blob = await fetch(uploadedFile as string).then((it) => it.blob());
		handleUpload(blob);
	};

	// I know this z-index is a hack, but we need to use this as the third-party color selector
	// is using a z-index
	return (
		<Popover center={center} visible origin={origin} zIndex={10}>
			<Modal>
				<Modal.Header
					onClose={() => onCancel()}
					title={`${t('common.personalInfo.images.uploadNew')} ${imageSpecification.title}`}
				/>

				<Modal.Contents>
					{error && <FormErrorMessage error={error} />}
					{uploadedFile === null && (
						<ImageUpload
							accept={imageSpecification.allowedExtensions}
							translationPropertyKey={imageSpecification.key}
							onError={(receivedError: Error) => setError(receivedError)}
							onFileSelected={(file: ImageFile) => setUploadedFile(file)}
							hasVideo={hasVideo}
						/>
					)}

					{uploadedFile !== null && isVideo && (
						<>
							<video controls>
								<source src={uploadedFile as string} />
							</video>

							<Box textAlign="center" mt={4}>
								<Flex justifyContent="flex-end">
									<Button onClick={uploadQuillMedia} title="Upload" />
								</Flex>
							</Box>
						</>
					)}

					{uploadedFile !== null && isSvg ? (
						<>
							<img style={{ maxWidth: '100%' }} src={uploadedFile as string} alt="" />
							<Button
								onClick={() => handleUpload(new Blob([uploadedFile!], { type: 'image/svg+xml' }))}
								title="Upload"
							/>
						</>
					) : (
						uploadedFile !== null &&
						!isVideo && (
							<ImageResizer
								fileString={uploadedFile as string}
								imageSpecification={imageSpecification}
								onImageResize={(fileBlob: Blob) => setResizedFileBlob(fileBlob)}
								onFinish={() => handleUpload(resizedFileBlob!)}
								onError={(receivedError: Error) => setError(receivedError)}
							/>
						)
					)}
				</Modal.Contents>
			</Modal>
		</Popover>
	);
}
